import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { PlayerListItemType } from '@/api/type/types';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import { fonts } from '@/theme/fonts';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import { formatDate } from '@/utils/date-time/formatDate';
import PlayerDetail from './PlayerDetail';
import { useLoading } from '@/context/LoadingContenxt';
import { getPlayerDetail } from '@/api/player-list/getPlayerDetail';
import { player } from '@/stores/slices/player-list/playerSlice';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { Images } from '@/assets';
import { useTranslation } from 'react-i18next';

interface PlayerListItemProps {
  items: PlayerListItemType[];
}

const PlayerListItem = ({ items }: PlayerListItemProps) => {
  const { t } = useTranslation();
  const columns = [
    { key: 'contactCountryCode', label: t('common.country'), width: '10%' },
    { key: 'loginId', label: 'ID', width: '22.5%' },
    {
      key: 'clinicName',
      label: t('playerList.office'),
      width: '10%',
    },
    { key: 'clinicContact', label: t('playerList.contact'), width: '15%' },
    {
      key: 'registeredAt',
      label: t('common.registeredDate'),
      width: '12.5%',
    },
  ] as const;

  const [openDetailMap, setOpenDetailMap] = useState<Record<string, boolean>>(
    {}
  );
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useAppDispatch();
  const { playerDetails } = useAppSelector((state) => state.player);

  const valueRenderMap: Record<
    (typeof columns)[number]['key'],
    (row: PlayerListItemType) => React.ReactNode
  > = {
    contactCountryCode: (row) =>
      getCountryNameByCode({
        contactCountryCode: row.clinicContact.contactCountryCode,
        countryCode: row.clinicContact.countryCode,
      }),
    loginId: (row) => row.loginId,
    clinicName: (row) => row.clinicName,
    clinicContact: (row) =>
      formatInternationalNumber({
        countryCode: row.clinicContact.contactCountryCode,
        number: row.clinicContact.contactNumber,
      }),
    registeredAt: (row) => formatDate({ dateString: row.registeredAt }),
  };

  const onClickDetail = useCallback(
    (id: string) => async () => {
      startLoading();
      try {
        const res = await getPlayerDetail({ playerId: id });
        if (res.isSuccess) {
          dispatch(player.setPlayerDetail({ id: id, data: res.data }));
          setOpenDetailMap((prev) => ({
            ...prev,
            [id]: !prev[id],
          }));
        }
      } finally {
        stopLoading();
      }
    },
    [dispatch, startLoading, stopLoading]
  );

  return (
    <TableWrap>
      <TableElement>
        <Thead>
          <tr>
            {columns.map((col) => (
              <Th key={col.key}>{col.label}</Th>
            ))}
          </tr>
        </Thead>
        {items.length > 0 && (
          <tbody>
            {items.map((row) => (
              <React.Fragment key={row.id}>
                <Tr onClick={onClickDetail(row.id)}>
                  {columns.map((col) => (
                    <Td key={col.key}>{valueRenderMap[col.key](row)}</Td>
                  ))}
                </Tr>
                {openDetailMap[row.id] && (
                  <DetailTr key={`detail-${row.id}`}>
                    <DetailTd colSpan={columns.length}>
                      <PlayerDetail
                        key={row.id}
                        detail={playerDetails[row.id]}
                      />
                    </DetailTd>
                  </DetailTr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        )}
      </TableElement>
      {items.length === 0 && (
        <NoneContainer>
          <NoneImg src={Images.NoneDataImg} alt="player-list-none" />
          <div>{t('common.noData')}</div>
        </NoneContainer>
      )}
    </TableWrap>
  );
};

export default PlayerListItem;

const TableWrap = styled.div`
  width: 100%;
  overflow: hidden;
  overflow-x: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: #eef2f7;
`;

const Th = styled.th`
  padding: 12px 16px;
  font-family: ${fonts.family.inter};
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  color: #6b7280;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 14px 16px;
  font-family: ${fonts.family.inter};
  font-size: 14px;
  color: #374151;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
`;

const Tr = styled.tr`
  cursor: pointer;
  &:hover {
    background: #eef2f7;
  }
`;

const DetailTd = styled.td`
  padding: 16px;
  background: #eef2f7;
  border-bottom: 1px solid #e5e7eb;
`;

const DetailTr = styled.tr``;

const NoneContainer = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 14px;
  color: #6b7280;
`;

const NoneImg = styled.img`
  width: 120px;
  height: 120px;
`;
